import { AgentDossier, FlightSearchResponseFlight, FlightSearchResponseFlightSegment, FlightSearchResponsePax, VendorType } from "@qite/tide-client";
import { first, isEmpty, last } from "lodash";
import { Maybe, TideAirline, TideAirport, TideCountry } from "../types";
import { ExtendedFlightSearchResponseItem, PaxType, PriceGroup } from "../site-types";
import { differenceInMinutes } from "date-fns";
import { useI18next } from "gatsby-plugin-react-i18next";

export const getOutwardFlight = (flightResult: ExtendedFlightSearchResponseItem): FlightSearchResponseFlight | undefined => {
  return flightResult?.outward;
};

export const getReturnFlight = (flightResult: ExtendedFlightSearchResponseItem): FlightSearchResponseFlight | undefined => {
  return flightResult?.return;
};

export const getFlightSegments = (flight: FlightSearchResponseFlight | undefined): FlightSearchResponseFlightSegment[] => {
  if (!flight) {
    return [];
  }
  return flight?.segments;
};

export const getDepartureSegment = (flight: FlightSearchResponseFlight | undefined): FlightSearchResponseFlightSegment | undefined => {
  const segments = getFlightSegments(flight);
  if (isEmpty(segments)) {
    return undefined;
  }

  return first(segments);
};

export const getArrivalSegment = (flight: FlightSearchResponseFlight | undefined): FlightSearchResponseFlightSegment | undefined => {
  const segments = getFlightSegments(flight);
  if (isEmpty(segments)) {
    return undefined;
  }

  return last(segments);
};

export const getOutwardDepartureSegment = (flightResult: ExtendedFlightSearchResponseItem): FlightSearchResponseFlightSegment | undefined => {
  const outwardFlight = getOutwardFlight(flightResult);

  return getDepartureSegment(outwardFlight);
};

export const getOutwardArrivalSegment = (flightResult: ExtendedFlightSearchResponseItem): FlightSearchResponseFlightSegment | undefined => {
  const outwardFlight = getOutwardFlight(flightResult);

  return getArrivalSegment(outwardFlight);
};

export const getReturnDepartureSegment = (flightResult: ExtendedFlightSearchResponseItem): FlightSearchResponseFlightSegment | undefined => {
  const returnFlight = getReturnFlight(flightResult);

  return getDepartureSegment(returnFlight);
};

export const getReturnArrivalSegment = (flightResult: ExtendedFlightSearchResponseItem): FlightSearchResponseFlightSegment | undefined => {
  const returnFlight = getReturnFlight(flightResult);

  return getArrivalSegment(returnFlight);
};

export const getNumberOfStops = (flight: FlightSearchResponseFlight | undefined): number => {
  const segments = getFlightSegments(flight);
  if (isEmpty(segments)) {
    return 0;
  }
  return segments.length - 1;
};

/**
 * Determines which "number of stops" filter bucket a flight belongs to (0, 1 or 2,
 * where 2 means "2 or more stops"). For a round trip a flight only qualifies for a
 * bucket when BOTH the outward and return leg have that number of stops; a flight
 * with a different number of stops per leg (e.g. 1 stop outward, 2 stops return)
 * matches no bucket and returns null. One-way flights are classified on the outward
 * leg alone. Used by both the stops filter and the price shown next to each option so
 * that the displayed price always matches the results the filter actually returns.
 */
export const getNumberOfStopsBucket = (result: ExtendedFlightSearchResponseItem, hasReturnLeg: boolean): number | null => {
  const stopsOutward = Math.min(getNumberOfStops(result.outward), 2);
  if (!hasReturnLeg) {
    return stopsOutward;
  }
  const stopsReturn = Math.min(getNumberOfStops(result.return), 2);
  return stopsOutward === stopsReturn ? stopsOutward : null;
};

export const getAirportName = (airport: TideAirport | undefined, language: string): string => {
  if (!airport || !airport.name) {
    return "";
  }
  const airportName = airport.localizedNames?.find((name) => name?.languageCode === language);
  return airportName && airportName?.value ? airportName.value : airport.name;
};

export const getAirportNameFromIata = (iata: string | undefined, language: string, airports: TideAirport[]): string => {
  if (!iata || isEmpty(airports)) {
    return "";
  }
  const airport = airports.find((a) => a.iata === iata);
  if (!airport || !airport.name) {
    return "";
  }

  const airportName = airport.localizedNames?.find((name) => name?.languageCode === language);
  return airportName && airportName?.value ? airportName.value : airport.name;
};

export const getAirportLocationName = (airport: TideAirport | undefined, language: string): string => {
  if (!airport || !airport.locationName) {
    return "";
  }
  const locationName = airport.localizedLocations?.find((name) => name?.languageCode === language);
  return locationName && locationName?.value ? locationName.value : airport.locationName;
};

export const getCountryName = (country: TideCountry | undefined | null, language: string): Maybe<string> | undefined => {
  if (!country) {
    return "";
  }

  const countryName = country.localizedNames?.find((name) => name?.languageCode === language);
  return countryName && countryName.value !== "" ? countryName.value : country.name;
};

export const getAirlineName = (iata: string | undefined, language: string, airlines: TideAirline[]): string => {
  if (!iata || isEmpty(airlines)) {
    return "";
  }
  const airline = airlines.find((a) => a.iata === iata);
  if (!airline) {
    return "";
  }
  return airline.name ?? iata;
};

export const getTravelerTicketNumber = (agentDossier: AgentDossier, travelerGuid: string | undefined) => {
  if (!travelerGuid) return "";
  const tickets = [];
  for (const entryLine of agentDossier.agentEntryLines) {
    if (!entryLine.flightMetaData) continue;
    for (const flightLine of entryLine.flightMetaData.flightLines) {
      const ticket = flightLine.ticketNumbers?.find((t) => t.travelerGuid === travelerGuid);
      if (ticket && ticket.number) tickets.push(ticket.number);
    }
  }
  return tickets.join(", ");
};

export const getProviderName = (vendorType: number | null): string => {
  switch (vendorType) {
    case VendorType.amadeus:
      return "Amadeus";
    case VendorType.airGateway:
      return "AirGateway";
    default:
      return "Unknown";
  }
};

export const mapFareTypeToName = (fareType: number | null): string => {
  switch (fareType) {
    case 0:
      return "PUB";
    case 1:
      return "TO";
    case 2:
      return "NEG";
    default:
      return "";
  }
};

export const getStopoverTime = (segments: FlightSearchResponseFlightSegment[], index: number) => {
  const { t } = useI18next();

  if (index === 0) return "";
  const previousSegment = segments[index - 1];
  const currentSegment = segments[index];
  const totalMinutes = differenceInMinutes(new Date(currentSegment.departureDateTime), new Date(previousSegment.arrivalDateTime));

  if (totalMinutes <= 0) {
    return "";
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours.toString() + t("SEARCH_RESULTS.HOURS") + minutes.toString().padStart(2, "0");
};

export const getPricesPerPaxType = (pricePerPax: FlightSearchResponsePax[]) => {
  const result: Record<PaxType, { taxPrice: number; ticketPrice: number; numberOfPax: number }> = {
    ADULT: { taxPrice: 0, ticketPrice: 0, numberOfPax: 0 },
    CHILD: { taxPrice: 0, ticketPrice: 0, numberOfPax: 0 },
    INFANT: { taxPrice: 0, ticketPrice: 0, numberOfPax: 0 },
  };

  if (!pricePerPax || isEmpty(pricePerPax)) {
    return [];
  }

  pricePerPax.forEach(({ age, taxPrice, ticketPrice }) => {
    let paxType: PaxType;
    if (age > 11) paxType = "ADULT";
    else if (age >= 2) paxType = "CHILD";
    else paxType = "INFANT";

    result[paxType].numberOfPax += 1;
    result[paxType].taxPrice = taxPrice;
    result[paxType].ticketPrice = ticketPrice;
  });

  return (Object.keys(result) as PaxType[])
    .map(
      (paxType) =>
        ({
          paxType,
          numberOfPax: result[paxType].numberOfPax,
          taxPrice: result[paxType].taxPrice,
          ticketPrice: result[paxType].ticketPrice,
        } as PriceGroup)
    )
    .filter((group) => group.numberOfPax > 0);
};
